// libs
import moment from 'moment'
// constants
import { ENTITIES } from 'constants/index'
import { REVIEW_STATUSES } from 'constants/review'
import { DEPTH_STEP } from './constants'
// types
import {
  IAssignedPerson,
  IChipEvent,
  IClosestEvent,
  INodeEntity,
  INodeEvents,
  IProjection,
  ITableTreeNode,
  IVisibleItem,
  TDropPosition
} from './types'
// utils
import { isEmpty } from 'lodash/fp'
import { generateReviews, getChipColor, getClosestRange, getClosestReview } from 'utils/index'

export const isHelperNode = (node: ITableTreeNode): boolean =>
  !!(node.isCreateRoot || node.isCreateObjective || node.isCreateAction || node.isCreateKPI)

export const defaultGetNodeTitle = (node: ITableTreeNode): string => node.title || ''

// Collects ids of nodes that come pre-expanded from the backend (`node.expanded`).
export const collectExpandedIds = (
  nodes: ITableTreeNode[],
  acc: Set<string | number> = new Set()
): Set<string | number> => {
  nodes.forEach((node) => {
    if (node.expanded) {
      acc.add(node.id)
    }
    if (node.children) {
      collectExpandedIds(node.children, acc)
    }
  })

  return acc
}

// ---- Immutable tree operations for drag & drop ----

// Removes the node with `id`; returns [treeWithoutNode, removedNode].
export const removeNode = (
  nodes: ITableTreeNode[],
  id: string | number
): [ITableTreeNode[], ITableTreeNode | null] => {
  let removed: ITableTreeNode | null = null

  const walk = (arr: ITableTreeNode[]): ITableTreeNode[] => {
    const result: ITableTreeNode[] = []

    arr.forEach((node) => {
      if (node.id === id) {
        removed = node
        return
      }
      result.push(node.children ? { ...node, children: walk(node.children) } : node)
    })

    return result
  }

  return [walk(nodes), removed]
}

// Inserts `node` as a child of `parentId` (root when null) right before `beforeId` (appended when null),
// expanding the parent so the inserted row is visible.
export const insertUnderParent = (
  nodes: ITableTreeNode[],
  parentId: string | number | null,
  beforeId: string | number | null,
  node: ITableTreeNode
): ITableTreeNode[] => {
  const insertInto = (siblings: ITableTreeNode[]): ITableTreeNode[] => {
    const arr = [...siblings]
    const index = beforeId != null ? arr.findIndex((item) => item.id === beforeId) : -1

    if (index === -1) {
      arr.push(node)
    } else {
      arr.splice(index, 0, node)
    }

    return arr
  }

  if (parentId == null) {
    return insertInto(nodes)
  }

  return nodes.map((item) => {
    if (item.id === parentId) {
      return { ...item, expanded: true, children: insertInto(item.children || []) }
    }
    return item.children ? { ...item, children: insertUnderParent(item.children, parentId, beforeId, node) } : item
  })
}

export const findNode = (nodes: ITableTreeNode[], id: string | number): ITableTreeNode | null => {
  for (const node of nodes) {
    if (node.id === id) {
      return node
    }
    if (node.children) {
      const found = findNode(node.children, id)

      if (found) {
        return found
      }
    }
  }
  return null
}

// True when `targetId` sits inside `node`'s subtree (blocks dropping a node into its own descendant).
export const isDescendant = (node: ITableTreeNode, targetId: string | number): boolean =>
  !!node.children && node.children.some((child) => child.id === targetId || isDescendant(child, targetId))

export const collectSubtreeIds = (
  node: ITableTreeNode,
  acc: Set<string | number> = new Set()
): Set<string | number> => {
  acc.add(node.id)
  node.children?.forEach((child) => collectSubtreeIds(child, acc))
  return acc
}

// Maps every node id → its structural parent id (null at root), across the whole tree.
export const buildParentMap = (
  nodes: ITableTreeNode[],
  parentId: string | number | null = null,
  map: Map<string | number, string | number | null> = new Map()
): Map<string | number, string | number | null> => {
  nodes.forEach((node) => {
    map.set(node.id, parentId)

    if (node.children) {
      buildParentMap(node.children, node.id, map)
    }
  })
  return map
}

// Ordered list of currently visible rows (respects collapse, skips helper nodes).
export const flattenVisible = (
  nodes: ITableTreeNode[],
  expanded: Set<string | number>,
  parentId: string | number | null = null,
  depth = 0,
  acc: IVisibleItem[] = []
): IVisibleItem[] => {
  nodes.forEach((node) => {
    if (isHelperNode(node)) {
      return
    }

    acc.push({ id: node.id, depth, parentId })

    if (node.children && expanded.has(node.id)) {
      flattenVisible(node.children, expanded, node.id, depth + 1, acc)
    }
  })
  return acc
}

export const getNodeDepth = (
  parentMap: Map<string | number, string | number | null>,
  id: string | number
): number => {
  let depth = 0
  let cursor = parentMap.get(id) ?? null

  while (cursor != null) {
    depth += 1
    cursor = parentMap.get(cursor) ?? null
  }
  return depth
}

// Projects the drop target (depth + parentId + sibling anchor) from the pointer's horizontal offset,
// following the dnd-kit "getProjection" model. `dragDeltaX` is the horizontal drag distance in px.
export const getProjection = (
  tree: ITableTreeNode[],
  expanded: Set<string | number>,
  parentMap: Map<string | number, string | number | null>,
  activeId: string | number,
  overId: string | number,
  position: TDropPosition,
  dragDeltaX: number
): IProjection | null => {
  const activeNode = findNode(tree, activeId)

  if (!activeNode) {
    return null
  }

  const activeSubtree = collectSubtreeIds(activeNode)
  const visible = flattenVisible(tree, expanded).filter((item) => !activeSubtree.has(item.id))

  const overPos = visible.findIndex((item) => item.id === overId)

  if (overPos === -1) {
    return null
  }

  const prevIndex = position === 'before' ? overPos - 1 : overPos
  const prev = visible[prevIndex]
  const next = position === 'before' ? visible[overPos] : visible[overPos + 1]

  const activeDepth = getNodeDepth(parentMap, activeId)
  const projectedDepth = activeDepth + Math.round(dragDeltaX / DEPTH_STEP)

  const maxDepth = prev ? prev.depth + 1 : 0
  const minDepth = next ? next.depth : 0

  let depth = projectedDepth

  if (depth > maxDepth) {
    depth = maxDepth
  } else if (depth < minDepth) {
    depth = minDepth
  }

  // Resolve parent from the projected depth relative to the previous visible row.
  let parentId: string | number | null = null

  if (depth > 0 && prev) {
    if (depth === prev.depth) {
      parentId = prev.parentId
    } else if (depth > prev.depth) {
      parentId = prev.id
    } else {
      const ancestor = visible
        .slice(0, prevIndex + 1)
        .reverse()
        .find((item) => item.depth === depth)
      parentId = ancestor ? ancestor.parentId : null
    }
  }

  // Sibling anchor: the direct child of `parentId` that `next` belongs to (insert before it), else append.
  let beforeId: string | number | null = null

  if (next) {
    let cursor: string | number | null = next.id

    while (cursor != null) {
      const cursorParent = parentMap.get(cursor) ?? null

      if (cursorParent === parentId) {
        beforeId = cursor
        break
      }
      cursor = cursorParent
    }
  }

  return { depth, parentId, beforeId }
}

// The next scheduled (not completed) review after `currentReview` — the one the review column shows.
const getNextReview = (entity: INodeEntity, currentReview: IClosestEvent): IClosestEvent | null => {
  const reviews: IClosestEvent[] = generateReviews({ entity })

  const upcoming = reviews
    .filter((review) => review.status !== REVIEW_STATUSES.COMPLETE)
    .filter((review) => review.dueDate?.isAfter(currentReview.dueDate, 'day'))
    .sort((a, b) => a.dueDate.valueOf() - b.dueDate.valueOf())

  return upcoming[0] || null
}

// Derives the per-node event chips/avatars (due date, KPI period, review, owner) from `node.entity`.
// Mirrors containers/ObjectivesList/components/NodeContent/layers/BL.
export const getNodeEvents = (node: ITableTreeNode): INodeEvents => {
  const entity = node.entity

  const isActionLike = node.entityType === ENTITIES.PLAN_OBJECTIVE || node.entityType === ENTITIES.PLAN_ACTION

  const action: IChipEvent | null =
    isActionLike && entity?.completionAt
      ? {
          chipColor: getChipColor(moment(entity.completionAt), entity.completionPercentage),
          chipLabel: moment(entity.completionAt).format('D MMM YY')
        }
      : null

  let period: IChipEvent | null = null

  if (node.entityType === ENTITIES.PLAN_KPI && entity) {
    const closestRange: IClosestEvent | null = getClosestRange(entity.recurrence, entity.resultTarget)

    if (closestRange) {
      period = {
        chipColor: getChipColor(closestRange.dueDate, closestRange.status),
        chipLabel: closestRange.dueDate.format('D MMM YY')
      }
    }
  }

  const closestReview: IClosestEvent = getClosestReview(entity)
  const review: IChipEvent | null = !isEmpty(closestReview)
    ? {
        chipColor: getChipColor(closestReview.dueDate, closestReview.status),
        chipLabel: closestReview.dueDate.format('D MMM YY')
      }
    : null

  // The first unfinished review scheduled after the one shown in the `review` column.
  const upcomingReview = !isEmpty(closestReview) ? getNextReview(entity, closestReview) : null
  const nextReview: IChipEvent | null = upcomingReview
    ? {
        chipColor: getChipColor(upcomingReview.dueDate, upcomingReview.status),
        chipLabel: upcomingReview.dueDate.format('D MMM YY')
      }
    : null

  let assignedPersons: IAssignedPerson[] = []

  if (entity?.ownedByUser) {
    assignedPersons = [{ user: entity.ownedByUser }]
  } else if (entity?.ownedByPosition) {
    assignedPersons = [{ position: entity.ownedByPosition }]
  }

  return { action, period, review, nextReview, assignedPersons }
}
